import { useEffect, useRef } from "react";

/**
 * Cinematic flame system:
 *  - Intro burst (~2.5s) emits particles from all 8 directions converging toward center.
 *  - Settles into ambient flame field (mostly bottom + light side drift) running indefinitely.
 *  - Respects prefers-reduced-motion.
 *  - Pauses when tab not visible.
 */
export default function FlameCanvasBackground() {
    const canvasRef = useRef(null);
    const stateRef = useRef({
        particles: [],
        embers: [],
        smoke: [],
        startTs: 0,
        introMs: 2500,
        running: true,
        reduced: false,
        rafId: 0,
        introDone: false,
    });

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: true });
        const dprCap = 2;
        const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        stateRef.current.reduced = reduced;

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
            canvas.width = Math.floor(window.innerWidth * dpr);
            canvas.height = Math.floor(window.innerHeight * dpr);
            canvas.style.width = window.innerWidth + "px";
            canvas.style.height = window.innerHeight + "px";
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener("resize", resize);

        const isMobile = window.innerWidth < 768;
        const TARGET_AMBIENT = reduced ? 30 : (isMobile ? 170 : 320);

        // pooled particle structure
        const makeParticle = () => ({
            x: 0, y: 0, vx: 0, vy: 0,
            life: 0, max: 1,
            size: 1, hue: 24,
            alpha: 0.9, blur: false, alive: false,
            mode: "ambient", // "intro" | "ambient" | "ember" | "smoke"
        });
        const POOL = new Array(800).fill(0).map(makeParticle);
        let nextIdx = 0;
        const acquire = () => {
            for (let i = 0; i < POOL.length; i++) {
                const idx = (nextIdx + i) % POOL.length;
                if (!POOL[idx].alive) { nextIdx = (idx + 1) % POOL.length; return POOL[idx]; }
            }
            return POOL[0];
        };

        const W = () => window.innerWidth;
        const H = () => window.innerHeight;

        // Spawn intro particles from 8 directions, converging toward center
        const spawnIntroBurst = (count) => {
            const cx = W() / 2;
            const cy = H() / 2;
            // 8 origin sectors: 4 edges + 4 corners
            const sectors = [
                () => ({ x: -20, y: Math.random() * H() }),                // left
                () => ({ x: W() + 20, y: Math.random() * H() }),           // right
                () => ({ x: Math.random() * W(), y: -20 }),                // top
                () => ({ x: Math.random() * W(), y: H() + 20 }),           // bottom
                () => ({ x: -20, y: -20 }),                                 // tl
                () => ({ x: W() + 20, y: -20 }),                            // tr
                () => ({ x: -20, y: H() + 20 }),                            // bl
                () => ({ x: W() + 20, y: H() + 20 }),                       // br
            ];
            for (let i = 0; i < count; i++) {
                const p = acquire();
                const o = sectors[i % sectors.length]();
                p.x = o.x; p.y = o.y;
                const dx = cx - p.x, dy = cy - p.y;
                const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                const speed = 7 + Math.random() * 9;
                p.vx = (dx / dist) * speed + (Math.random() - 0.5) * 1.5;
                p.vy = (dy / dist) * speed + (Math.random() - 0.5) * 1.5;
                p.size = 6 + Math.random() * 14;
                p.hue = 12 + Math.random() * 33; // red->orange-yellow
                p.alpha = 0.85 + Math.random() * 0.15;
                p.life = 0;
                p.max = 60 + Math.random() * 40;
                p.blur = Math.random() < 0.35;
                p.alive = true;
                p.mode = "intro";
            }
        };

        const spawnAmbient = () => {
            // Bottom-up flames
            const w = W(), h = H();
            const p = acquire();
            p.x = Math.random() * w;
            p.y = h + 10 + Math.random() * 20;
            p.vx = (Math.random() - 0.5) * 0.6;
            p.vy = -(0.6 + Math.random() * 1.4);
            p.size = 6 + Math.random() * 16;
            p.hue = 12 + Math.random() * 32;
            p.alpha = 0.6 + Math.random() * 0.35;
            p.life = 0;
            p.max = 100 + Math.random() * 80;
            p.blur = Math.random() < 0.3;
            p.alive = true;
            p.mode = "ambient";
        };

        const spawnEmber = () => {
            // small gold spark drifting up
            const p = acquire();
            p.x = Math.random() * W();
            p.y = H() + Math.random() * 40;
            p.vx = (Math.random() - 0.5) * 0.8;
            p.vy = -(0.8 + Math.random() * 1.6);
            p.size = 1 + Math.random() * 1.8;
            p.hue = 38 + Math.random() * 8;
            p.alpha = 0.85;
            p.life = 0;
            p.max = 180 + Math.random() * 120;
            p.blur = false;
            p.alive = true;
            p.mode = "ember";
        };

        const spawnSideDrift = () => {
            const fromLeft = Math.random() < 0.5;
            const p = acquire();
            p.x = fromLeft ? -10 : W() + 10;
            p.y = H() * 0.4 + Math.random() * H() * 0.6;
            p.vx = (fromLeft ? 1 : -1) * (0.6 + Math.random() * 1.0);
            p.vy = -(0.4 + Math.random() * 1.0);
            p.size = 5 + Math.random() * 10;
            p.hue = 16 + Math.random() * 28;
            p.alpha = 0.5 + Math.random() * 0.4;
            p.life = 0;
            p.max = 80 + Math.random() * 60;
            p.blur = Math.random() < 0.25;
            p.alive = true;
            p.mode = "ambient";
        };

        // Intro setup: spawn a heavy initial burst
        const introBurstCount = reduced ? 40 : (isMobile ? 180 : 320);
        spawnIntroBurst(introBurstCount);
        stateRef.current.startTs = performance.now();

        const onVisibility = () => {
            stateRef.current.running = document.visibilityState !== "hidden";
            if (stateRef.current.running && stateRef.current.introDone) {
                stateRef.current.startTs = performance.now() - (stateRef.current.introMs + 100);
                requestAnimationFrame(loop);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);

        const draw = () => {
            const w = W(), h = H();
            // "Trail" wipe to retain glow without full clear (lighter so flames are more visible)
            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = "rgba(0, 0, 0, 0.14)";
            ctx.fillRect(0, 0, w, h);

            ctx.globalCompositeOperation = "lighter";

            for (let i = 0; i < POOL.length; i++) {
                const p = POOL[i];
                if (!p.alive) continue;

                p.life++;
                if (p.life > p.max) { p.alive = false; continue; }

                // physics
                p.x += p.vx;
                p.y += p.vy;

                if (p.mode === "intro") {
                    // decelerate as they reach center, dissipate
                    p.vx *= 0.965;
                    p.vy *= 0.965;
                    p.size *= 0.985;
                } else {
                    // ambient flicker
                    p.vx += (Math.random() - 0.5) * 0.06;
                    p.vy -= 0.02 + Math.random() * 0.02;
                    if (p.mode === "ember") {
                        p.vy -= 0.005;
                    }
                }

                const lifeRatio = p.life / p.max;
                let a = p.alpha * (1 - lifeRatio);
                if (a <= 0) continue;

                const radius = Math.max(0.5, p.size * (1 - lifeRatio * 0.65));
                const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 4);
                const sat = 100;
                const li = 60 - lifeRatio * 30;
                grad.addColorStop(0, `hsla(${p.hue},${sat}%,${li + 10}%,${a})`);
                grad.addColorStop(0.4, `hsla(${p.hue + 4},${sat}%,${li - 5}%,${a * 0.6})`);
                grad.addColorStop(1, `hsla(${p.hue},${sat}%,${li - 20}%,0)`);
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(p.x, p.y, radius * 4, 0, Math.PI * 2);
                ctx.fill();

                if (p.blur) {
                    ctx.fillStyle = `hsla(45,100%,80%,${a * 0.45})`;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, radius * 0.7, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            ctx.globalCompositeOperation = "source-over";
        };

        let lastSpawnTs = 0;

        const loop = (ts) => {
            if (!stateRef.current.running) return;
            const elapsed = ts - stateRef.current.startTs;
            const inIntro = elapsed < stateRef.current.introMs;

            // Continue feeding intro for the first ~1.2s
            if (inIntro) {
                if (elapsed < 1100 && Math.random() < (reduced ? 0.05 : 0.4)) {
                    spawnIntroBurst(reduced ? 6 : 16);
                }
            } else if (!stateRef.current.introDone) {
                stateRef.current.introDone = true;
            }

            // Ambient spawn loop (also runs lightly during intro)
            const ambientFactor = inIntro ? 0.35 : 1;
            const targetAlive = TARGET_AMBIENT * ambientFactor;
            // count alive ambient/ember
            let aliveAmbient = 0;
            for (let i = 0; i < POOL.length; i++) {
                if (POOL[i].alive && POOL[i].mode !== "intro") aliveAmbient++;
            }
            const need = Math.max(0, Math.floor(targetAlive - aliveAmbient));
            const spawnPerFrame = Math.min(need, reduced ? 1 : 6);
            for (let i = 0; i < spawnPerFrame; i++) spawnAmbient();

            // sparks + side drift
            if (!reduced) {
                if (Math.random() < 0.5) spawnEmber();
                if (Math.random() < 0.18) spawnSideDrift();
            }

            draw();
            stateRef.current.rafId = requestAnimationFrame(loop);
        };
        stateRef.current.rafId = requestAnimationFrame(loop);

        const state = stateRef.current;
        return () => {
            cancelAnimationFrame(state.rafId);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            data-testid="flame-canvas"
            aria-hidden="true"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: -2,
                pointerEvents: "none",
                background:
                    "radial-gradient(80% 60% at 50% 110%, rgba(255,69,0,0.22), rgba(0,0,0,0) 60%), #000",
            }}
        />
    );
}
